import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { Check, X, CheckCircle2, XCircle, RefreshCw } from 'lucide-react';
import { useLocation } from '@/shared/hooks/useLocation';
import {
  useAuditOverview,
  useRefreshAudit,
  useAuditStatus,
  useCancelAudit,
} from '../hooks/useAudit';
import { integrationsApi } from '@/modules/settings/api/integrationsApi';
import { integrationKeys } from '@/modules/settings/queryKeys';
import { Gauge } from '@/shared/ui';
import { SectionBar } from '../components/SectionBar';
import type { AuditResult } from '../api/seoApi';
import { AuditProgressPanel } from '../components/AuditProgressPanel';
import { SkeletonCard } from '@/shared/ui/Skeleton';
import { PageHeader } from '@/shared/ui';
import styles from './SEODashboardPage.module.css';

const SECTION_LABELS: Record<string, string> = {
  technical_seo: 'Technical SEO',
  on_page_seo: 'On-Page SEO & Content',
  local_seo: 'Local SEO & GBP',
  off_page_seo: 'Off-Page SEO & Authority',
  schema_structured_data: 'Schema & Structured Data',
  aeo_ai_visibility: 'AEO & AI Visibility',
  conversion_ux: 'Patient Digital Experience',
};

function AiReadinessCard({ readiness }: { readiness: AuditResult['aiReadiness'] }) {
  if (!readiness) return null;
  const overall = readiness.overall as string;
  const overallClass =
    overall === 'high'
      ? styles.statValueSuccess
      : overall === 'medium'
        ? styles.statValueWarning
        : styles.statValueError;
  const overallBg =
    overall === 'high'
      ? 'var(--success-bg)'
      : overall === 'medium'
        ? 'var(--warning-bg)'
        : 'var(--error-bg)';

  return (
    <div className={styles.aiReadinessCard}>
      <div className={styles.aiReadinessHeader}>
        <div>
          <div className={styles.aiReadinessTitle}>AI Readiness Assessment</div>
          <div className={styles.aiReadinessSub}>
            How likely AI answer engines are to cite your practice
          </div>
        </div>
        <span
          className={clsx(styles.aiReadinessBadge, overallClass)}
          style={{ background: overallBg }}
        >
          {overall} Readiness
        </span>
      </div>

      <div className={styles.aiEnginesGrid}>
        {(
          (readiness.engines ?? []) as Array<{
            name: string;
            likelihood: string;
            reasoning: string;
          }>
        ).map((e) => {
          const likelihoodClass =
            e.likelihood === 'low'
              ? styles.statValueError
              : e.likelihood === 'medium'
                ? styles.statValueWarning
                : styles.statValueSuccess;
          return (
            <div key={e.name} className={styles.aiEngineCard}>
              <div className={styles.aiEngineHeader}>
                <span className={styles.aiEngineName}>{e.name}</span>
                <span className={clsx(styles.aiEngineLikelihood, likelihoodClass)}>
                  {e.likelihood}
                </span>
              </div>
              <div className={styles.aiEngineReasoning}>{e.reasoning}</div>
            </div>
          );
        })}
      </div>

      <div className={styles.aiFactorsGrid}>
        <div>
          <div className={styles.aiFactorTitleHelping}>Helping</div>
          <div className={styles.aiFactorList}>
            {((readiness.factors_positive ?? []) as string[]).map((f, i) => (
              <div key={i} className={clsx(styles.aiFactorItem, styles.aiFactorItemPositive)}>
                <CheckCircle2 size={14} strokeWidth={2} aria-hidden />
                <span>{f}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className={styles.aiFactorTitleHurting}>Hurting</div>
          <div className={styles.aiFactorList}>
            {((readiness.factors_negative ?? []) as string[]).map((f, i) => (
              <div key={i} className={clsx(styles.aiFactorItem, styles.aiFactorItemNegative)}>
                <XCircle size={14} strokeWidth={2} aria-hidden />
                <span>{f}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function SEODashboardPage() {
  const { currentLocation } = useLocation();
  const overviewQuery = useAuditOverview();
  const { mutate: refreshAudit, isPending: isRefreshing } = useRefreshAudit();
  const statusQuery = useAuditStatus();
  const { mutate: cancelAudit, isPending: isCancelling } = useCancelAudit();
  const gscQuery = useQuery({
    queryKey: integrationKeys.gscStatusShort(currentLocation?.id ?? ''),
    queryFn: () => integrationsApi.gsc.getStatus(currentLocation!.id).then((r) => r.data),
    enabled: !!currentLocation?.id,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const overview = overviewQuery.data;
  const audit = overview?.latestAudit;

  const sectionScores = audit?.sectionScores ?? null;
  const summary = audit?.executiveSummary ?? null;
  const aiReadiness = audit?.aiReadiness ?? null;

  const auditStatus = statusQuery.data?.status;
  const isAuditActive = auditStatus === 'pending' || auditStatus === 'in_progress';
  const [panelDismissed, setPanelDismissed] = useState(false);

  // Auto-hide the panel once the audit finishes successfully. A failure stays
  // visible until the user dismisses it so the error is actually seen.
  useEffect(() => {
    if (auditStatus === 'completed') {
      setPanelDismissed(true);
    }
  }, [auditStatus]);

  const showProgressPanel =
    !panelDismissed && !!statusQuery.data?.progress && (isAuditActive || auditStatus === 'failed');

  const handleRefresh = () => {
    setPanelDismissed(false);
    refreshAudit();
  };

  return (
    <div className={styles.page}>
      <PageHeader
        title="Site Audit"
        subtitle="Detailed audit scores, section breakdowns, and AI readiness assessment"
        timestamp={audit?.createdAt}
        actions={
          <button
            type="button"
            className={styles.refreshBtn}
            onClick={handleRefresh}
            disabled={isRefreshing || isAuditActive}
          >
            <RefreshCw size={14} aria-hidden />
            {isRefreshing ? 'Starting…' : isAuditActive ? 'Auditing…' : 'Refresh Audit'}
          </button>
        }
      />

      {showProgressPanel && (
        <AuditProgressPanel
          steps={statusQuery.data!.progress!}
          status={auditStatus}
          errorMessage={statusQuery.data?.errorMessage}
          onCancel={() => cancelAudit()}
          isCancelling={isCancelling}
          onDismiss={() => setPanelDismissed(true)}
        />
      )}

      {overviewQuery.isLoading ? (
        <div className={styles.skeletonStack}>
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : (
        <>
          {/* Metrics row — 5 equal cards */}
          <div className={styles.metricsRow}>
            {/* SEO/AEO Readiness */}
            <div className={styles.metricCardCenter}>
              <span className={styles.metricLabel}>SEO/AEO Readiness</span>
              <Gauge
                score={audit?.overallScore ?? null}
                grade={audit?.grade ?? null}
                size={120}
                showGrade="inline"
              />
            </div>

            {/* Organic Traffic */}
            {(() => {
              const gsc = gscQuery.data;
              const connected = gsc?.connected && gsc.totalClicks != null;
              let trendPct: number | null = null;
              if (connected && gsc!.prevTotalClicks != null && gsc!.prevTotalClicks > 0) {
                trendPct = Math.round(
                  ((gsc!.totalClicks! - gsc!.prevTotalClicks) / gsc!.prevTotalClicks) * 100,
                );
              }
              return (
                <div className={styles.metricCardCenter}>
                  <div className={styles.metricLabel}>Organic Traffic</div>
                  <div className={styles.metricBig}>
                    {connected ? gsc!.totalClicks!.toLocaleString() : '—'}
                  </div>
                  {connected && <div className={styles.metricNote}>clicks / 28d</div>}
                  <div className={styles.metricTrendSlot}>
                    {trendPct != null ? (
                      <span
                        className={clsx(
                          styles.trendPill,
                          trendPct >= 0 ? styles.trendPillUp : styles.trendPillDown,
                        )}
                      >
                        {trendPct >= 0 ? '↑' : '↓'} {Math.abs(trendPct)}% vs prev 28d
                      </span>
                    ) : connected ? (
                      <span className={styles.metricNote}>via Google Search Console</span>
                    ) : (
                      <span className={styles.metricNote}>Connect GSC for data</span>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Indexed Pages, Trust Score, AI Readiness */}
            {[
              {
                label: 'Indexed Pages',
                value:
                  overview?.sitemapUrlCount != null
                    ? overview.sitemapUrlCount.toLocaleString()
                    : '—',
                sub: overview?.sitemapUrlCount != null ? 'pages' : '',
                note: overview?.sitemapUrlCount != null ? 'from sitemap' : 'run an audit to count',
              },
              {
                label: 'Trust Score',
                value: audit?.trustScore != null ? String(Math.round(audit.trustScore)) : '—',
                sub: '/100',
                note: 'citations + reviews + E-E-A-T',
              },
              {
                label: 'AI Readiness',
                value: aiReadiness?.overall
                  ? aiReadiness.overall.charAt(0).toUpperCase() + aiReadiness.overall.slice(1)
                  : '—',
                sub: '',
                note: audit?.sectionScores?.aeo_ai_visibility
                  ? `${audit.sectionScores.aeo_ai_visibility.checks.filter((c) => c.status === 'pass').length} of 14 AEO checks pass`
                  : '',
                isAlert: aiReadiness?.overall === 'low',
              },
            ].map((m, i) => (
              <div key={i} className={styles.metricCard}>
                <div className={styles.metricLabel}>{m.label}</div>
                <div className={styles.metricValueRow}>
                  <span className={clsx(styles.metricValue, m.isAlert && styles.metricValueAlert)}>
                    {m.value}
                  </span>
                  <span className={styles.metricSub}>{m.sub}</span>
                </div>
                {m.note && <div className={styles.metricNote}>{m.note}</div>}
              </div>
            ))}
          </div>

          {/* Top wins + urgent fixes */}
          {summary && (
            <div className={styles.winsFixesGrid}>
              <div className={styles.winsCard}>
                <div className={styles.winsTitle}>
                  <Check size={14} aria-hidden /> Top Wins
                </div>
                {(summary.top_3_wins ?? []).map((w, i) => (
                  <div key={i} className={styles.winsItem}>
                    {w}
                  </div>
                ))}
              </div>
              <div className={styles.fixesCard}>
                <div className={styles.fixesTitle}>
                  <X size={14} aria-hidden /> Urgent Fixes
                </div>
                {(summary.top_3_urgent_fixes ?? []).map((f, i) => (
                  <div key={i} className={styles.fixesItem}>
                    {f}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Section scores */}
          {sectionScores && (
            <div className={styles.sectionScoresCard}>
              <div className={styles.sectionScoresTitle}>Section Scores</div>
              {Object.entries(sectionScores)
                .sort(([, a], [, b]) => b.weight - a.weight)
                .map(([key, section]) => {
                  const passed = section.checks.filter((c) => c.status === 'pass').length;
                  return (
                    <SectionBar
                      key={key}
                      name={SECTION_LABELS[key] ?? key}
                      weight={section.weight}
                      score={section.score}
                      passed={passed}
                      total={section.checks.length}
                      checks={section.checks}
                    />
                  );
                })}
            </div>
          )}

          {/* AI Readiness */}
          {aiReadiness && <AiReadinessCard readiness={aiReadiness} />}

          {/* Owner summary */}
          {summary?.owner_friendly_summary && (
            <div className={styles.summaryCard}>
              <div className={styles.summaryTitle}>Audit Summary</div>
              <p className={styles.summaryBody}>{summary.owner_friendly_summary}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
